import { useState } from 'react';
import { Upload, Download, MapPin, Wrench, FlaskConical, Building2, Flag } from 'lucide-react';
import ClinicMatrixImportTab from '../components/cutover/ClinicMatrixImportTab';
import ClinicMatrixExportTab from '../components/cutover/ClinicMatrixExportTab';
import GeocodingTab from '../components/cutover/GeocodingTab';
import LabCorpImportTab from '../components/cutover/LabCorpImportTab';
import ClinicImportTab from '../components/cutover/ClinicImportTab';
import JobSiteImportTab from '../components/cutover/JobSiteImportTab';
import { usePermissions, ClinicMgmtPermissions } from '@shared/context/usePermissions';
import './AdminCutoverPage.css';

type CutoverTab = 'matrix' | 'matrixExport' | 'geocoding' | 'labcorp' | 'clinicImport' | 'jobSiteImport';

export default function AdminCutoverPage() {
  const [activeTab, setActiveTab] = useState<CutoverTab>('matrix');
  const { hasPermission } = usePermissions();
  const canRunClinicImport = hasPermission(ClinicMgmtPermissions.RunClinicBulkImport);
  // SLV2-4126: a cutover operator who can also create JobSites (the API requires both).
  const canRunJobSiteImport =
    hasPermission(ClinicMgmtPermissions.CreateJobSite) &&
    hasPermission(ClinicMgmtPermissions.RunClinicMatrixImport);

  return (
    <div className="acp">
      {/* Header */}
      <div className="acp-header">
        <div className="acp-header-icon">
          <Wrench size={22} />
        </div>
        <div className="acp-header-text">
          <h1>Cutover Tools</h1>
          <p className="acp-subtitle">One-time bulk operations for initial environment setup</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="acp-tabs">
        <button
          type="button"
          className={`acp-tab ${activeTab === 'matrix' ? 'active' : ''}`}
          onClick={() => setActiveTab('matrix')}
        >
          <Upload size={16} />
          <span>Clinic Matrix</span>
        </button>
        <button
          type="button"
          className={`acp-tab ${activeTab === 'matrixExport' ? 'active' : ''}`}
          onClick={() => setActiveTab('matrixExport')}
        >
          <Download size={16} />
          <span>Matrix Export</span>
        </button>
        <button
          type="button"
          className={`acp-tab ${activeTab === 'geocoding' ? 'active' : ''}`}
          onClick={() => setActiveTab('geocoding')}
        >
          <MapPin size={16} />
          <span>Geocoding</span>
        </button>
        <button
          type="button"
          className={`acp-tab ${activeTab === 'labcorp' ? 'active' : ''}`}
          onClick={() => setActiveTab('labcorp')}
        >
          <FlaskConical size={16} />
          <span>LabCorp Import</span>
        </button>
        {canRunClinicImport && (
          <button
            type="button"
            className={`acp-tab ${activeTab === 'clinicImport' ? 'active' : ''}`}
            onClick={() => setActiveTab('clinicImport')}
          >
            <Building2 size={16} />
            <span>Clinic Import</span>
          </button>
        )}
        {canRunJobSiteImport && (
          <button
            type="button"
            className={`acp-tab ${activeTab === 'jobSiteImport' ? 'active' : ''}`}
            onClick={() => setActiveTab('jobSiteImport')}
          >
            <Flag size={16} />
            <span>JobSite Import</span>
          </button>
        )}
      </div>

      {/* Tab Content */}
      <div className="acp-content">
        {activeTab === 'matrix' && <ClinicMatrixImportTab />}
        {activeTab === 'matrixExport' && <ClinicMatrixExportTab />}
        {activeTab === 'geocoding' && <GeocodingTab />}
        {activeTab === 'labcorp' && <LabCorpImportTab />}
        {activeTab === 'clinicImport' && canRunClinicImport && <ClinicImportTab />}
        {activeTab === 'jobSiteImport' && canRunJobSiteImport && <JobSiteImportTab />}
      </div>
    </div>
  );
}
